import * as THREE from 'three';
import type { BattleState } from '@somnhold/sim';
import { GlowLayer } from '../engine/glow.ts';
import { lookPoint, looksRng, Precip, SkyLook, TAU, type OmenCtx, type OmenScene } from './common.ts';
import { GroundMarks, MARK } from './marks.ts';

/**
 * Starfall: a deep blue night with silver motes drifting and shooting stars streaking over the board. A
 * star of the sim shows where it will land (a pale ring, a shaft of light), falls in an arc with a long
 * tail, lands in a burst of white light and lies there pulsing, a crystal of light with turning rays,
 * until a hero takes it: the light runs into them and they shine, sparks circling, while it lasts.
 */

const WHITE = new THREE.Color('#ffffff');
const SILVER = new THREE.Color('#cfe0ff');
const BLUE = new THREE.Color('#7fa8ff');
const VIOLET = new THREE.Color('#a98bff');

export const buildStarfall = (ctx: OmenCtx): OmenScene => {
  const group = new THREE.Group();
  group.name = 'omen-starfall';
  const low = ctx.low;
  const rnd = looksRng(0x57a7);
  const sky = new SkyLook(ctx, {
    hemi: ['#8f9ad8', '#1d1a30', 0.8],
    key: ['#c4d0ff', 0.75],
    rim: ['#a98bff', 1.4],
    background: '#0d0f1f',
    fog: '#10122a',
    mist: { low: '#3a3c66', mid: '#262a4c', far: '#0d0f1f', alpha: 1.1 },
  });
  const marks = new GroundMarks(24, 8);
  const glow = new GlowLayer(1024);
  const motes = new Precip({ count: low ? 90 : 260, box: [13, 5, 13], fall: -0.18, sway: 0.4, size: [0.06, 0.06], color: '#d8e4ff', opacity: 0.8, round: true, additive: true, twinkle: 3.5, lift: 0.2 }, 77);
  group.add(marks.group, motes.mesh, glow.mesh);

  /** Stars of the sim: warning (and its longest), where, when it began to fall (frame clock). */
  const warnOf = new Map<number, { max: number; from: THREE.Vector3; seen: number }>();
  let falling: { id: number; p: THREE.Vector3; r: number; k: number; from: THREE.Vector3 }[] = [];
  let lying: { id: number; p: THREE.Vector3; r: number; left: number }[] = [];
  /** Heroes shining with a star (id → ticks left). */
  let shining: { id: number; left: number }[] = [];
  const meteors: { a: THREE.Vector3; b: THREE.Vector3; t0: number; dur: number }[] = [];
  let nextMeteor = 1;
  let now = 0;
  let tickAt = 0;
  let flashAt = -10;
  const look = new THREE.Vector3();
  const tmp = new THREE.Vector3();

  const tick = (s: BattleState): void => {
    tickAt = now;
    falling = [];
    lying = [];
    const seen = new Set<number>();
    for (const z of s.zones) {
      if (z.kind !== 'star') continue;
      seen.add(z.id);
      const p = ctx.at(z.x, z.y, 0.02);
      const r = (z.radius / 1000) * 1.2;
      if ((z.warn ?? 0) > 0) {
        let w = warnOf.get(z.id);
        if (!w) {
          // It comes from high up beyond the board (the top of the screen), swinging in from one side or the other.
          const side = (rnd() - 0.5) * 10;
          w = { max: z.warn as number, from: p.clone().add(new THREE.Vector3(side, ctx.crypt ? 5 : 8, ctx.crypt ? -3 : -6)), seen: now };
          warnOf.set(z.id, w);
        }
        w.max = Math.max(w.max, z.warn as number);
        falling.push({ id: z.id, p, r, k: 1 - (z.warn as number) / w.max, from: w.from });
      } else lying.push({ id: z.id, p, r, left: z.ticks });
    }
    for (const id of [...warnOf.keys()]) if (!seen.has(id)) warnOf.delete(id);
    marks.begin();
    for (const f of falling) marks.add(MARK.starTarget, f.p, f.r, f.k, (f.id % 31) / 31);
    for (const l of lying) marks.add(MARK.starTarget, l.p, l.r, 0.35 + 0.15 * Math.sin(now * 3), (l.id % 31) / 31);
    marks.end();
    shining = s.heroes.filter((h) => h.downTicks === 0 && (h.starUntil ?? 0) > s.tick).map((h) => ({ id: h.id, left: (h.starUntil as number) - s.tick }));
    for (const sh of shining) {
      const p = ctx.pos(sh.id);
      if (p && rnd() < 0.6) ctx.vfx.burst(tmp.copy(p).setY(p.y + 0.2 + rnd() * 0.5), { n: 1, color: rnd() < 0.5 ? '#e8f0ff' : '#9fb8ff', speed: 0.01, up: 0.05, size: 0.07, life: 10, gravity: -0.002, spread: 0.4 });
    }
    for (const l of lying) if (rnd() < 0.5) ctx.vfx.burst(tmp.copy(l.p).setY(l.p.y + 0.3), { n: 1, color: '#dfe8ff', speed: 0.015, up: 0.06, size: 0.06, life: 12, gravity: -0.002, spread: l.r * 0.6 });
  };

  const land = (p: THREE.Vector3, r: number): void => {
    const up = (h: number): THREE.Vector3 => p.clone().setY(p.y + h);
    ctx.flare(up(0.5), '#ffffff', 2.2, 4);
    ctx.flare(up(0.4), '#9fb8ff', r * 3.4, 12);
    ctx.vfx.ring(p, r * 0.1, r * 1.6, '#e8f0ff', 8, 1);
    ctx.vfx.ring(up(0.04), r * 0.05, r * 1.1, '#8f9cff', 11, 0.8);
    ctx.vfx.burst(up(0.3), { n: low ? 14 : 34, color: '#f0f5ff', speed: 0.17, up: 0.16, size: 0.08, life: 12, gravity: 0.012 });
    ctx.vfx.burst(up(0.3), { n: low ? 8 : 18, color: '#a98bff', speed: 0.12, up: 0.2, size: 0.1, life: 14, gravity: 0.01 });
    ctx.vfx.burst(up(0.2), { n: low ? 4 : 8, color: '#2a2d44', speed: 0.05, up: 0.05, size: 0.6, life: 16, smoke: true, spread: r * 0.5 });
    marks.put(MARK.crater, p, r * 0.9, 30, rnd());
    ctx.shake(0.2);
    flashAt = now;
  };

  /** The star taken: its light runs up into the hero in a column and a ring. */
  const taken = (p: THREE.Vector3, heroAt: THREE.Vector3 | undefined): void => {
    const h = heroAt ?? p;
    ctx.vfx.implode(h.clone().setY(h.y + 0.4), 1.2, { n: low ? 10 : 22, color: '#e8f0ff', size: 0.1, life: 8, swirl: 0.8, lift: 0.6 });
    ctx.vfx.shaft(h.clone().setY(h.y), h.clone().setY(h.y + 3.2), '#b8ccff', 10, 0.35);
    ctx.vfx.ring(h.clone().setY(h.y + 0.03), 0.2, 1.4, '#cfe0ff', 9, 1);
    ctx.flare(h.clone().setY(h.y + 0.7), '#ffffff', 1.8, 6);
    ctx.vfx.burst(p.clone().setY(p.y + 0.3), { n: low ? 8 : 16, color: '#cfe0ff', speed: 0.08, up: 0.12, size: 0.08, life: 10 });
  };

  return {
    group,
    tick,
    event: (s, e) => {
      if (e.t === 'boom' && e.kind === 'starfall') {
        land(ctx.at(e.x, e.y, 0.02), (e.r / 1000) * 1.2);
        return true;
      }
      if (e.t === 'skill' && e.skill === 'starPick') {
        taken(ctx.at(e.x, e.y, 0.02), ctx.pos(e.hero) ?? (() => {
          const h = s.heroes.find((x) => x.id === e.hero);
          return h ? ctx.at(h.x, h.y) : undefined;
        })());
        return true;
      }
      return false;
    },
    frame: (t, camera) => {
      now = t;
      marks.frame(t);
      lookPoint(camera, look);
      motes.frame(t, look);
      const fl = Math.max(0, 1 - (t - flashAt) / 0.45);
      sky.pulse({ hemi: 1 + 1.3 * fl, key: 1 + 1.1 * fl, rim: 1 + fl });
      glow.begin();
      // Shooting stars over the board now and then: a bright head and a tail that fades behind it.
      if (!ctx.crypt && t > nextMeteor) {
        const a = rnd() * TAU;
        const from = look.clone().add(new THREE.Vector3(Math.cos(a) * 12, 7 + rnd() * 3, Math.sin(a) * 12));
        const to = look.clone().add(new THREE.Vector3(-Math.cos(a) * (4 + rnd() * 8), 3 + rnd() * 2, -Math.sin(a) * (4 + rnd() * 8)));
        meteors.push({ a: from, b: to, t0: t, dur: 0.7 + rnd() * 0.5 });
        nextMeteor = t + (low ? 2.5 : 1.2) + rnd() * 2.5;
      }
      for (let i = meteors.length - 1; i >= 0; i--) {
        const m = meteors[i] as (typeof meteors)[number];
        const k = (t - m.t0) / m.dur;
        if (k > 1.3) {
          meteors.splice(i, 1);
          continue;
        }
        const fade = k > 1 ? 1 - (k - 1) / 0.3 : Math.min(1, k * 4);
        for (let j = 0; j < 10; j++) {
          const kk = Math.min(1, k) - j * 0.025;
          if (kk < 0) break;
          tmp.copy(m.a).lerp(m.b, kk);
          const f = 1 - j / 10;
          glow.add(tmp.x, tmp.y, tmp.z, j === 0 ? WHITE : SILVER, (j === 0 ? 0.35 : 0.22) * f, 0.9 * f * fade);
        }
      }
      // Falling stars of the sim: along their arc by how far the warning has run (smoothed between ticks).
      const sub = Math.min(1, (t - tickAt) * 12);
      for (const f of falling) {
        const w = warnOf.get(f.id);
        const step = w ? 1 / w.max : 0;
        const k = Math.min(1, f.k + step * sub);
        const head = tmp.copy(f.from).lerp(f.p, k * k);
        head.y = f.p.y + (f.from.y - f.p.y) * (1 - k * k) + 0.3;
        // Its tail, back along the way it came.
        for (let j = 0; j < 30; j++) {
          const kj = Math.max(0, k - j * 0.016);
          const q = new THREE.Vector3().copy(f.from).lerp(f.p, kj * kj);
          q.y = f.p.y + (f.from.y - f.p.y) * (1 - kj * kj) + 0.3;
          const fj = 1 - j / 30;
          glow.add(q.x, q.y, q.z, j < 4 ? WHITE : j < 12 ? SILVER : VIOLET, 0.45 * (0.35 + fj * 0.65), 0.7 * fj);
        }
        glow.add(head.x, head.y, head.z, WHITE, 0.75, 1);
        glow.add(head.x, head.y, head.z, BLUE, 1.7, 0.35);
        // The shaft that marks where it lands.
        glow.add(f.p.x, f.p.y + 0.15, f.p.z, SILVER, f.r * (0.6 + k), 0.12 + 0.3 * k);
      }
      // Stars lying on the ground: a crystal of light, turning rays, a halo; it blinks out at the end.
      for (const l of lying) {
        const blink = l.left < 24 ? 0.5 + 0.5 * Math.sin(t * 18) : 1;
        const pulse = 0.85 + 0.15 * Math.sin(t * 4 + l.id);
        const y = l.p.y + 0.45 + Math.sin(t * 2.2 + l.id) * 0.06;
        glow.add(l.p.x, y, l.p.z, WHITE, 0.4 * pulse, blink);
        glow.add(l.p.x, y, l.p.z, SILVER, 0.8 * pulse, 0.45 * blink);
        glow.add(l.p.x, y, l.p.z, BLUE, 1.7, 0.18 * blink);
        for (let k = 0; k < 6; k++) {
          const a = t * 0.9 + (k / 6) * TAU;
          const d = 0.28 + 0.05 * Math.sin(t * 3 + k);
          glow.add(l.p.x + Math.cos(a) * d, y + Math.sin(a * 2) * 0.05, l.p.z + Math.sin(a) * d, SILVER, 0.12, 0.8 * blink);
        }
        glow.add(l.p.x, l.p.y + 0.06, l.p.z, VIOLET, l.r * 1.4, 0.14 * blink);
      }
      // Heroes shining with a star: a halo at their feet, sparks circling them, fading as it runs out.
      for (const sh of shining) {
        const p = ctx.pos(sh.id);
        if (!p) continue;
        const k = Math.min(1, sh.left / 24);
        glow.add(p.x, p.y + 0.08, p.z, BLUE, 1.5, 0.3 * k);
        glow.add(p.x, p.y + 0.6, p.z, SILVER, 1.1, 0.25 * k);
        for (let j = 0; j < 3; j++) {
          const a = t * 3.2 + (j / 3) * TAU;
          glow.add(p.x + Math.cos(a) * 0.42, p.y + 0.35 + 0.25 * Math.sin(t * 2.4 + j * 2), p.z + Math.sin(a) * 0.42, WHITE, 0.16, 0.95 * k);
          glow.add(p.x + Math.cos(a - 0.25) * 0.42, p.y + 0.35 + 0.25 * Math.sin(t * 2.4 + j * 2 - 0.2), p.z + Math.sin(a - 0.25) * 0.42, SILVER, 0.1, 0.5 * k);
        }
      }
      glow.end();
    },
    dispose: () => {
      sky.restore();
      marks.dispose();
      motes.dispose();
      glow.mesh.geometry.dispose();
      glow.material.dispose();
    },
  };
};
